import { createSteeringURL } from '@/api'
import { getUser, oidcManager, OIDCNoUserError } from '@/oidc'
import { AnyPermission } from '@/types'
import { APIConsume, ensureError, useRetrieveHelpers } from '@/util'
import { deferred } from '@rokoli/bnb/common'
import {
  APICreate,
  APIListPaginated,
  APIRemove,
  APIResponseError,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
} from '@rokoli/bnb/drf'
import { fromUnixTime } from 'date-fns'
import { User as OIDCUser } from 'oidc-client-ts'
import { defineStore } from 'pinia'
import {
  computed,
  MaybeRefOrGetter,
  readonly,
  ref,
  shallowReadonly,
  toValue,
  watch,
  WatchOptions,
} from 'vue'
import { components as steeringTypes } from '../steering-types'

const log = globalThis.console

export type CurrentUser = {
  name: string
  email: string
  oidcAccessToken: string
}

type SessionInitializationState = 'OIDC_AUTH' | 'STEERING_INITIALIZATION' | undefined

export type SteeringUser = Required<steeringTypes['schemas']['User']>

export const useUserStore = defineStore('users', () => {
  const { api, base } = createExtendableAPI<SteeringUser>(createSteeringURL.prefix('users'), {
    getRequestDefaults() {
      const authStore = useAuthStore()
      return authStore.currentUser
        ? { headers: { Authorization: `Bearer ${authStore.currentUser.oidcAccessToken}` } }
        : {}
    },
  })
  const listOperations = APIListPaginated(api)
  const consumeOperations = APIConsume(listOperations.listIsolated)
  const retrieveOperations = APIRetrieve(api)

  useOnAuthBehaviour(() => void consumeOperations.consume())

  return {
    ...base,
    ...listOperations,
    ...consumeOperations,
    ...retrieveOperations,
    ...APICreate(api),
    ...APIUpdate(api),
    ...APIRemove(api),
    ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
  }
})

export function useOnAuthBehaviour(
  behaviour: (user?: SteeringUser) => unknown | Promise<unknown>,
  options?: WatchOptions,
) {
  const authStore = useAuthStore()
  const done = deferred<boolean>()
  const watchHandle = watch(
    () => authStore.steeringUser,
    async (user) => {
      if (user) {
        await behaviour(user)
        deferred.resolve(done, true)
      }
    },
    { immediate: true, ...options },
  )
  return { watch: watchHandle, done }
}

export type Authorizer = (userPermissions: AnyPermission[]) => boolean

function _hasUserPermission(
  user: SteeringUser | undefined | null,
  requiredPermissions: AnyPermission[] | Authorizer,
) {
  if (user?.isPrivileged) return true
  const userPermissions = (user?.permissions ?? []) as AnyPermission[]
  const permissionChecker = requiredPermissions
  if (typeof permissionChecker === 'function') return permissionChecker(userPermissions)
  else {
    return (
      permissionChecker.length === 0 ||
      permissionChecker.every((perm) => userPermissions.includes(perm))
    )
  }
}

export function useHasUserPermission(
  requiredPermissions: MaybeRefOrGetter<AnyPermission[] | Authorizer>,
) {
  const authStore = useAuthStore()
  return computed(() => {
    return _hasUserPermission(authStore.steeringUser, toValue(requiredPermissions))
  })
}

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<CurrentUser>()
  const _steeringUser = ref<SteeringUser>()
  const steeringUser = computed(() => (currentUser.value ? _steeringUser.value : undefined))
  const sessionInitializationState = ref<SessionInitializationState>()
  const isInitializingSession = computed(() => sessionInitializationState.value !== undefined)
  const authError = ref<Error | null>(null)

  function setCurrentUser(user: OIDCUser) {
    currentUser.value = {
      name: user.profile.nickname ?? '',
      email: user.profile.email ?? '',
      oidcAccessToken: user.access_token,
    }
    if (user.expires_at)
      console.debug(`Current client token will expire at ${fromUnixTime(user.expires_at)}`)
  }

  async function loadUser() {
    sessionInitializationState.value = 'OIDC_AUTH'
    const oidcUser = await getUser()
    sessionInitializationState.value = 'STEERING_INITIALIZATION'
    const userStore = useUserStore()
    const user = await userStore.retrieve(parseInt(oidcUser.profile.sub), {
      requestInit: {
        headers: { Authorization: `Bearer ${oidcUser.access_token}` },
      },
    })

    if (user?.permissions) {
      const permissions = user.permissions.map((p) => `\t${p}`).join('\n')
      console.debug(`${user.username} is logged in. Provided permissions are:\n${permissions}`)
    }

    setCurrentUser(oidcUser)
    _steeringUser.value = user ?? undefined
  }

  oidcManager.events.addUserLoaded(setCurrentUser)
  oidcManager.events.addUserSignedOut(async () => {
    log.debug('User has signed out. Resetting auth store.')
    currentUser.value = undefined
  })
  oidcManager.events.addAccessTokenExpired(() => {
    log.debug('OIDC token has expired. Logging out...')
    currentUser.value = undefined
  })

  async function init() {
    authError.value = null
    log.debug('Initializing oidc client')

    try {
      await loadUser()
    } catch (err) {
      log.debug('Could not load user data.', err)
      currentUser.value = undefined
      _steeringUser.value = undefined
      if (err instanceof OIDCNoUserError) return
      if (err instanceof APIResponseError && err.response.status === 401) return
      authError.value = ensureError(err)
    } finally {
      sessionInitializationState.value = undefined
      void (await import('@/router')).redirectToLogin()
    }
  }

  function hasUserPermission(requiredPermissions: AnyPermission[] | Authorizer) {
    return _hasUserPermission(_steeringUser.value, requiredPermissions)
  }

  function isOwner(ownedBy: SteeringUser['id'][], privilegedOwnsEverything = true) {
    const user = steeringUser.value
    if (!user) return false
    return ownedBy.includes(user.id) || (privilegedOwnsEverything && user.isPrivileged)
  }

  return {
    currentUser: shallowReadonly(currentUser),
    steeringUser,
    sessionInitializationState: readonly(sessionInitializationState),
    isInitializingSession: readonly(isInitializingSession),
    hasUserPermission,
    authError,
    isOwner,
    init,
  }
})

export const steeringAuthInit: { getRequestDefaults: () => RequestInit } = {
  getRequestDefaults() {
    const authStore = useAuthStore()
    return {
      headers: {
        Authorization: `Bearer ${authStore.currentUser?.oidcAccessToken}`,
      },
    }
  },
}
